<template>
  <content-box class="change-plan--wrap" :width="'530px'">
    <p class="title">
      {{ $t('common.chargePv') }}
    </p>
    <div class="plan-type--wrap">
      <div class="plan-type" :class="paymentType">
        <button type="button"
                class="card"
                :class="{on: paymentType === 'card'}"
                :style="{opacity: ($i18n.locale !== 'ko' && paymentType === 'wireTransfer') ? 0.5 : 1}"
                @click="onSelectPaymentType('card')">
          <i class="icon icon--check-white-size20"></i>{{ $t('userInfo.managePlan.planSubscription.button.card') }}
        </button>
        <button type="button"
                class="enterprise"
                :class="{on: paymentType === 'wireTransfer'}"
                :style="{opacity: ($i18n.locale !== 'ko' && paymentType === 'card') ? 0.5 : 1}"
                @click="onSelectPaymentType('wireTransfer')">
          <i class="icon icon--check-white-size20"></i>{{ $t('userInfo.managePlan.planSubscription.button.wire') }}
        </button>
      </div>
    </div>
    <div class="plan-info--wrap">
      <div class="pv-price">
        <p class="label">
          <strong>{{ pvAmount | thousand }}</strong> PV
          <span class="period">
            {{ $t('common.subscriptionTerms') }}: {{ period.start | dashDate }} ~ {{ period.end | dashDate }}
          </span>
        </p>
        <div class="value">
          <p>₩ {{ pvPrice | thousand }}</p>
          <span>({{ $t('common.vat') }})</span>
        </div>
      </div>
      <div class="pv-button">
        <ui-button :iconName="'icon--minus-black'"
                   :size="'large'"
                   @click="setChargeAmount(-1)"></ui-button>
        <input class="pv-text"
               type="'text'"
               v-model="chargeAmount"
               @input="setChargeAmount" />
        <ui-button :iconName="'icon--plus-black'"
                   :size="'large'"
                   @click="setChargeAmount(1)"></ui-button>
      </div>
    </div>
    <div class="notice--wrap">
      <ul>
        <li>
          <p>{{ $t('userInfo.managePlan.chargePv.notice.minimumAmount') }}</p>
        </li>
        <li>
          <p>{{ $t('userInfo.managePlan.chargePv.notice.reloadedImmediately') }}</p>
        </li>
        <li>
          <p>{{ $t('userInfo.managePlan.chargePv.notice.unitPrice') }}</p>
        </li>
        <li>
          <p>{{ $t('userInfo.managePlan.chargePv.notice.notBeCarried') }}</p>
        </li>
      </ul>
    </div>
    <div class="footer">
      <ui-checkbox v-model="isTermsAgree" v-if="paymentType === 'card'">
        <span class="terms-agree">
          {{ $t('userInfo.managePlan.changePlan.footer.checkbox') }}
        </span>
      </ui-checkbox>
      <span class="terms-agree" v-else>
        {{ $t('userInfo.managePlan.planSubscription.wireDescription') }}
      </span>
      <span v-if="hasTermsError" class="terms-agree-validation-text">{{ $t('common.validationErrorText.requiredAgree') }}</span>
      <div class="button--wrap">
        <ui-button class="btn__cancel"
                   @click="onCloseLayer">{{ $t('common.cancel') }}</ui-button>
        <ui-button class="btn__confirm"
                   :primary="true"
                   @click="onConfirm"
                   v-if="paymentType === 'card' && $i18n.locale === 'ko'">
          {{ $t('common.confirm') }}
        </ui-button>
        <ui-button class="btn__confirm"
                   :primary2="true"
                   @click="onConfirm"
                   v-else>
          {{ $t('userInfo.managePlan.planSubscription.button.contact') }}
        </ui-button>
      </div>
    </div>
  </content-box>
</template>

<script>
import { addDays } from 'date-fns'
import { openContactUs, getLocaleDateStr, thousand } from '@/libs/utils'
import ContentBox from '@/components/ui/ContentBox'
import UiButton from '@/components/ui/UiButton'
import UiCheckbox from '@/components/ui/UiCheckbox'

export default {
  components: {
    ContentBox,
    UiButton,
    UiCheckbox
  },
  props: [
    'userData',
    'planData',
    'newPlanData'
  ],
  data () {
    return {
      hasTermsError: false,
      isTermsAgree: false,
      paymentType: 'card',
      chargeAmount: 1,
      period: {
        start: null,
        end: null
      }
    }
  },
  computed: {
    pvAmount () {
      return 100000 * this.chargeAmount
    },
    pvPrice () {
      return 1.5 * this.pvAmount
    },
    expireDateFormat () {
      return getLocaleDateStr(addDays(this.planData.endDate, 1))
    }
  },
  created () {
    this.period.start = new Date()
    if (this.$i18n.locale !== 'ko') {
      this.paymentType = 'wireTransfer'
    }
    // this.period.end = this.planData.endDate
    this.period.end = this.planData.monthlyEndDate
  },
  methods: {
    setChargeAmount (value) {
      if (value) {
        this.chargeAmount = this.chargeAmount + value
      }
      this.chargeAmount = parseInt(this.chargeAmount) || 1
      if (this.chargeAmount > 10000000) {
        this.chargeAmount = 10000000
      } else if (this.chargeAmount < 1) {
        this.chargeAmount = 1
      }
    },
    onSelectPaymentType (value) {
      if (this.$i18n.locale === 'ko') {
        this.hasTermsError = false
        this.paymentType = value
      }
    },
    canChargePv () {
      if (this.planData.isTerminated || this.planData.isTrial ||
        this.planData.isTrialOver || this.planData.isUnpaid ||
        !this.planData.hasPlan || this.planData.useCoupon ||
        this.$i18n.locale === 'ja') {
        return false
      } else {
        return true
      }
    },
    onCloseLayer () {
      this.$emit('closeLayer')
    },
    onConfirm () {
      this.hasTermsError = false

      if (this.paymentType !== 'card' || this.$i18n.locale !== 'ko') {
        if (this.$i18n.locale === 'ko') {
          openContactUs(this.userData.userNum,
            this.$t('userInfo.managePlan.default.wireForm', {
              product: this.$t('common.chargePv'),
              amount: thousand(this.chargeAmount),
              pv: thousand(this.pvAmount),
              price: thousand(this.pvPrice)
            }), true
          )
          this.onCloseLayer()
          return
        } else if (this.$i18n.locale === 'en') {
          openContactUs(this.userData.userNum,
            this.$t('userInfo.managePlan.default.wireForm', {
              product: this.$t('common.chargePv'),
              amount: thousand(this.chargeAmount),
              pv: thousand(this.pvAmount),
              price: thousand(this.chargeAmount * 300)
            }), true
          )
          this.onCloseLayer()
          return
        } else {
          openContactUs(this.userData.userNum,
            this.$t('userInfo.managePlan.default.wireForm', {
              product: this.$t('common.chargePv'),
              amount: thousand(this.chargeAmount),
              pv: thousand(this.pvAmount),
              price: thousand(this.chargeAmount * 22500)
            }), true
          )
          this.onCloseLayer()
          return
        }
      }

      if (!this.isTermsAgree) {
        this.hasTermsError = true
        return
      }

      this.$emit('onConfirm', {
        amount: this.chargeAmount,
        pv: this.pvAmount,
        price: this.pvPrice
      })
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.change-plan--wrap {
  padding: 30px 30px 25px;
  box-sizing: border-box;

  .plan-type--wrap {
    margin-top: 24px;
    text-align: center;

    .plan-type {
      display: inline-block;
      width: 430px;
      border: 1px solid #e0e0e0;
      background-color: #ffffff;
      transition: background-color 0.3s ease, border-color 0.3s ease;
      border-radius: 20px;
      position: relative;

      button {
        width: 210px;
        text-align: center;
        line-height: 36px;
        height: 36px;
        color: #888888;
        position: relative;
        font-size: 15px;
        z-index: 2;
        transition: color 0.3s ease;

        &:first-child {
          box-sizing: border-box;
          padding-right: 5px;
        }

        &:last-child {
          box-sizing: border-box;
          padding-right: 5px;
        }

        i {
          vertical-align: middle;
          transform: translateY(-1px);
          width: 0;
          transition: width 0.3s ease, margin 0.3s ease;
        }

        &.on {
          color: #ffffff;

          i {
            width: 20px;
            margin: 0 20px 0 0;
          }
        }
      }

      &::before {
        content: "";
        position: absolute;
        z-index: 1;
        top: -1px;
        left: -1px;
        background: #ec0147;
        border-radius: 20px;
        transition: left 0.3s cubic-bezier(0.25, 1, 0.5, 1);
        display: block;
        width: 220px;
        height: 38px;
        box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.25);
      }

      &.wireTransfer::before {
        left: 211px;
      }
    }
  }

  .title {
    font-size: 15px;
    font-weight: bold;
    color: #2f2f2f;
  }

  .plan-info--wrap {
    margin-top: 33px;
    padding: 30px;
    box-sizing: border-box;
    border-radius: 5px;
    border: solid 1px #d7d7d7;
    background-color: #f4f4f4;

    .pv-price {
      @include flexXbetween;
      align-items: center;

      .label {
        font-size: 16px;
        color: #ec0047;

        strong {
          font-size: 28px;
          font-weight: bold;
        }

        .period {
          font-size: 13px;
          color: #666666;
          display: block;
          margin-top: 10px;
        }
      }

      .value {
        text-align: right;
        p {
          font-size: 16px;
          font-weight: bold;
          color: #2f2f2f;
        }
        span {
          display: inline-block;
          font-size: 12px;
          color: #444444;
          margin-top: 8px;
        }
      }
    }

    .pv-button {
      border-top: 2px solid #c1c1c1;
      margin-top: 30px;
      padding-top: 25px;
      text-align: center;

      .pv-text {
        -webkit-appearance: none;
        width: 70px;
        height: 38px;
        border-radius: 6px;
        border: 1px solid #289eef;
        background-color: #ffffff;
        vertical-align: middle;
        text-align: center;
        font-size: 16px;
        color: #2f2f2f;
        margin: 0 5px;
      }

      button {
        min-width: 0;
        padding-right: 2px;
        vertical-align: middle;
      }
    }
  }

  .notice--wrap {
    margin-top: 25px;

    ul > li {
      position: relative;
      padding-left: 20px;
      font-size: 13px;
      line-height: 1.46;
      color: #666666;

      &:before {
        content: '';
        display: inline-block;
        width: 3px;
        height: 3px;
        background-color: #888;
        position: absolute;
        top: 7px;
        left: 10px;
      }

      &:not(:first-child) {
        margin-top: 10px;
      }

      ol {
        margin-top: 10px;

        li {
          @include flexXbetween;

          &:not(:first-child) {
            margin-top: 5px;
          }

          button {
            font-size: 12px;
            font-weight: bold;
            text-align: right;
            color: #555555;
          }
        }
      }
    }
  }

  .footer {
    margin-top: 25px;
    padding-top: 25px;
    border-top: 1px solid #d8d8d8;
    text-align: center;
    position: relative;

    .button--wrap {
      margin-top: 40px;

      button {
        min-width: 86px;
      }

      .btn__confirm {
        margin-left: 5px;
      }
    }

    .ui_checkbox {
      margin-left: 0;
    }
    .terms-agree {
      position: relative;
      text-align: left;
      font-size: 13px;
      color: #2f2f2f;
    }
    .terms-agree-validation-text {
      position: absolute;
      top: 50px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 13px;
      color: $brandColor;
      white-space: nowrap;
      display: inline-block;
    }
  }
}
</style>
